import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomTabNav from '../shared/components/BottomTabNav';
import Footer from '../shared/components/Footer';
import './CustomerPersonalInfo.css';

const CustomerPersonalInfo = () => {
  const { user } = useAuth();
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    postalCode: '',
    memberSince: '',
    accountStatus: 'Active'
  });

  useEffect(() => {
    if (user) {
      setUserInfo({
        name: user.user_metadata?.name || 'Not provided',
        email: user.email || 'Not provided',
        phone: user.user_metadata?.phone || 'Not provided',
        dateOfBirth: user.user_metadata?.date_of_birth || 'Not provided',
        address: user.user_metadata?.address || 'Not provided',
        city: user.user_metadata?.city || 'Not provided',
        postalCode: user.user_metadata?.postal_code || 'Not provided',
        memberSince: user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }) : 'Unknown',
        accountStatus: 'Active'
      });
    }
  }, [user]);

  const formatDisplayValue = (value) => {
    return value === 'Not provided' ? (
      <span className="not-provided">Not provided</span>
    ) : value;
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(' ', '-');
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  return (
    <div className="App">
      <div className="personal-info-container">
        <div className="personal-info-content">
          {/* Header */}
          <div className="personal-info-header">
            <Link to="/profile" className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <h1 className="personal-info-title">Personal Information</h1>
            <Link to="/edit-profile" className="edit-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>

          {/* Profile Summary */}
          <div className="profile-summary">
            <div className="profile-avatar">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="40" fill="url(#personalInfoGradient)"/>
                <text 
                  x="40" 
                  y="48" 
                  textAnchor="middle" 
                  fontSize="28" 
                  fontWeight="700" 
                  fill="white"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {userInfo.name?.charAt(0)?.toUpperCase() || 'U'}
                </text>
                <defs>
                  <linearGradient id="personalInfoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea"/>
                    <stop offset="100%" stopColor="#764ba2"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="profile-summary-content">
              <h2 className="profile-name">{userInfo.name}</h2>
              <p className="profile-email">{userInfo.email}</p>
              <div className="profile-status">
                {getStatusBadge(userInfo.accountStatus)}
                <span className="member-since">Member since {userInfo.memberSince}</span>
              </div>
            </div>
          </div>

          {/* Information Sections */}
          <div className="info-sections">
            {/* Basic Information */}
            <div className="info-section">
              <h3 className="section-title">
                <span className="section-icon">👤</span>
                Basic Information
              </h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Full Name</label>
                  <span className="info-value">{formatDisplayValue(userInfo.name)}</span>
                </div>
                <div className="info-item">
                  <label>Email Address</label>
                  <span className="info-value">{formatDisplayValue(userInfo.email)}</span>
                </div>
                <div className="info-item">
                  <label>Phone Number</label>
                  <span className="info-value">{formatDisplayValue(userInfo.phone)}</span>
                </div>
                <div className="info-item">
                  <label>Date of Birth</label>
                  <span className="info-value">{formatDisplayValue(userInfo.dateOfBirth)}</span>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="info-section">
              <h3 className="section-title">
                <span className="section-icon">📍</span>
                Address Information
              </h3>
              <div className="info-grid">
                <div className="info-item full-width">
                  <label>Street Address</label>
                  <span className="info-value">{formatDisplayValue(userInfo.address)}</span>
                </div>
                <div className="info-item">
                  <label>City</label>
                  <span className="info-value">{formatDisplayValue(userInfo.city)}</span>
                </div>
                <div className="info-item">
                  <label>Postal Code</label>
                  <span className="info-value">{formatDisplayValue(userInfo.postalCode)}</span>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="info-section">
              <h3 className="section-title">
                <span className="section-icon">🔐</span>
                Account Information
              </h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Account Status</label>
                  <span className="info-value">{getStatusBadge(userInfo.accountStatus)}</span>
                </div>
                <div className="info-item">
                  <label>Member Since</label>
                  <span className="info-value">{userInfo.memberSince}</span>
                </div>
                <div className="info-item">
                  <label>Account Type</label>
                  <span className="info-value">
                    <span className="account-type premium">Premium Customer</span>
                  </span>
                </div>
                <div className="info-item">
                  <label>Last Updated</label>
                  <span className="info-value">
                    {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            {/* Privacy & Security */}
            <div className="info-section">
              <h3 className="section-title">
                <span className="section-icon">🛡️</span>
                Privacy & Security
              </h3>
              <div className="security-items">
                <div className="security-item">
                  <div className="security-content">
                    <h4>Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <div className="security-status">
                    <span className="status-badge inactive">Not Enabled</span>
                  </div>
                </div>
                
                <div className="security-item">
                  <div className="security-content">
                    <h4>Login Notifications</h4>
                    <p>Get notified when someone logs into your account</p>
                  </div>
                  <div className="security-status">
                    <span className="status-badge active">Enabled</span>
                  </div>
                </div>
                
                <div className="security-item">
                  <div className="security-content">
                    <h4>Data Sharing</h4>
                    <p>Control how your data is used for personalization</p>
                  </div>
                  <div className="security-status">
                    <span className="status-badge active">Limited</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <Link to="/edit-profile" className="primary-action-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Edit Profile
            </Link>
            
            <Link to="/privacy" className="secondary-action-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 22S2 18 2 12V5L12 2L22 5V12C22 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Privacy Settings
            </Link>
          </div>
        </div>
      </div>
      
      <Footer websiteType="customer" />
      <BottomTabNav />
    </div>
  );
};

export default CustomerPersonalInfo;
